import {
  AutopilotTaskDto,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client';
import {
  AgxAnimatedToggleLoaderOverlay,
  AgxAnimatedToggleLoaderState,
  type AgxAnimatedToggleLoaderStateValue,
  AgxLoadingState,
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  type StepsIndicatorState,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import TaskModalActionButtons from './TaskModalActionButtons';
import { CampaignDetailModel, DocumentGroup } from '@urbanx/agx.campaigns.client';
import TasksFilesList from 'pages/campaigns/components/campaignTasks/TasksFilesList';
import { TaskModalPageProps } from './TaskModalPageProps';
import { emitTaskUpdate, useAutopilotTaskUpdates } from 'hooks/useAutopilotTaskUpdates';
import { applyTaskUpdateToTasks } from 'utils/tasks';
import { TaskUpdatedNotification } from 'types/pushNotifications';
import TaskDueDateBadge from 'components/tasks/TaskDueDateBadge';
import {
  isWorkflowConcurrencyError,
  useWorkflowConcurrencyError,
} from 'hooks/useWorkflowConcurrencyError';
import {
  createTaskModalPages,
  resolveTaskModalPageCompletion,
  type TaskModalPage as TaskModalPageDefinition,
} from './taskModalPageStatus';
import './TaskModalShell.scss';

export interface TaskModalShellProps {
  title: string;
  tasks: AutopilotTaskDto[];
  open: boolean;
  onClose: () => void;
  campaignId: string;
  campaign: CampaignDetailModel;
  documents: DocumentGroup[];
  isPending?: boolean;
  onSaveAllPages?: (tasks: AutopilotTaskDto[]) => void | Promise<void>;
  renderPage: (props: TaskModalPageProps) => React.ReactNode;
  onCompleteSuccess?: () => void;
  incompletePageTaskIds?: string[];
  saveAndExitDisabled?: boolean;
  initialPageIndex?: number;
  modalId?: string;
  resolvePages?: (tasks: AutopilotTaskDto[]) => TaskModalPageDefinition[];
  hideCloseButton?: boolean;
  previousButtonText?: string;
  presentationClassName?: string;
}

const TaskModalPage = ({
  renderPage,
  pageProps,
}: {
  renderPage: TaskModalShellProps['renderPage'];
  pageProps: TaskModalPageProps;
}) => renderPage(pageProps);

const TaskModalShell = ({
  title,
  tasks,
  open,
  onClose,
  campaignId,
  campaign,
  documents,
  isPending = false,
  onSaveAllPages,
  renderPage,
  onCompleteSuccess,
  incompletePageTaskIds = [],
  saveAndExitDisabled = false,
  initialPageIndex = 0,
  modalId,
  resolvePages = createTaskModalPages,
  hideCloseButton = false,
  previousButtonText = 'Back',
  presentationClassName,
}: TaskModalShellProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [highestVisitedPage, setHighestVisitedPage] = useState(0);
  const [localTasks, setLocalTasks] = useState<AutopilotTaskDto[]>(tasks);
  const [isPersistingCurrentPage, setIsPersistingCurrentPage] = useState(false);
  const isPersistingCurrentPageRef = useRef(false);
  const [completionLoaderState, setCompletionLoaderState] =
    useState<AgxAnimatedToggleLoaderStateValue>();
  const beforeTaskModalSaveRef = useRef<(() => boolean | Promise<boolean>) | undefined>(undefined);
  const taskModalPageValidationRef = useRef<(() => boolean | Promise<boolean>) | undefined>(
    undefined
  );
  const persistedTasksRef = useRef<AutopilotTaskDto[]>(tasks);
  const persistingTaskRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const wasOpenRef = useRef(open);
  const queryClient = useQueryClient();
  const initialPages = resolvePages(tasks);
  const resolvedInitialPageIndex = Math.min(
    Math.max(initialPageIndex, 0),
    Math.max(initialPages.length - 1, 0)
  );

  useEffect(() => {
    if (!open) {
      setLocalTasks(tasks);
      persistedTasksRef.current = tasks;
      setCurrentPage(resolvedInitialPageIndex);
      setHighestVisitedPage(resolvedInitialPageIndex);
    }
  }, [tasks, open, resolvedInitialPageIndex]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setLocalTasks(tasks);
      persistedTasksRef.current = tasks;
      setCurrentPage(resolvedInitialPageIndex);
      setHighestVisitedPage(resolvedInitialPageIndex);
    }

    wasOpenRef.current = open;
  }, [open, tasks, resolvedInitialPageIndex]);

  const pages = resolvePages(localTasks);
  const completedPages = resolveTaskModalPageCompletion(pages, incompletePageTaskIds);
  const pageTasks = pages.map(({ task }) => task);
  const currentTask = pageTasks[currentPage];
  const titleTask = currentTask ?? pageTasks[0];
  const completionReady = completedPages.length > 0 && completedPages.every(Boolean);

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();

  const { pushToast } = useToast();
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();

  const applyTaskUpdate = (taskUpdate: TaskUpdatedNotification) => {
    persistedTasksRef.current = applyTaskUpdateToTasks(persistedTasksRef.current, taskUpdate);
    if (isPersistingCurrentPageRef.current) return;

    setLocalTasks((prevTasks) => applyTaskUpdateToTasks(prevTasks, taskUpdate));
  };

  useAutopilotTaskUpdates(applyTaskUpdate);

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: (updatedTask) => {
      if (!updatedTask) return;

      queryClient.setQueryData<AutopilotTaskDto[]>(
        ['getAutopilotTasksForCampaign', campaignId, updatedTask.contractId ?? undefined],
        (previousTasks) =>
          previousTasks
            ? applyTaskUpdateToTasks(previousTasks, {
                task: updatedTask,
                collectionStateChanged: false,
              })
            : previousTasks
      );
      emitTaskUpdate({ task: updatedTask, collectionStateChanged: false });
    },
    onError: async (error) => {
      if (
        persistingTaskRef.current &&
        (await handleWorkflowConcurrencyError(error, persistingTaskRef.current, {
          restore: () => setLocalTasks(persistedTasksRef.current),
        }))
      ) {
        return;
      }

      pushToast({
        type: 'error',
        message: 'Failed to toggle task steps',
      });
    },
  });

  const toggleTaskStep = (stepOrder: number, isChecked: boolean) => {
    if (!currentTask?.id) {
      throw new Error(`Task modal page ${currentPage + 1} is missing a task`);
    }

    const binding = pages[currentPage]?.stepBindings.find(
      (candidate) => candidate.pageStepOrder === stepOrder
    );

    if (!binding) {
      throw new Error(`Task modal page ${currentTask.id} has no binding for step ${stepOrder}`);
    }

    toggleTaskStepForTask(binding.taskId, binding.taskStepOrder, isChecked);
  };

  const toggleTaskStepForTask = (taskId: string, stepOrder: number, isChecked: boolean) => {
    setLocalTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) {
          return task;
        }

        return {
          ...task,
          taskSteps:
            task.taskSteps?.map((step) =>
              step.order === stepOrder ? { ...step, isCompleted: isChecked } : step
            ) ?? [],
        };
      })
    );
  };

  const registerBeforeTaskModalSave = (handler: (() => boolean | Promise<boolean>) | undefined) => {
    beforeTaskModalSaveRef.current = handler;
  };

  const registerTaskModalPageValidation = (
    handler: (() => boolean | Promise<boolean>) | undefined
  ) => {
    taskModalPageValidationRef.current = handler;
  };

  const canPersistCurrentPage = async () => {
    const beforeTaskModalSave = beforeTaskModalSaveRef.current;

    if (!beforeTaskModalSave) return true;

    return await beforeTaskModalSave();
  };

  const isCurrentPageValid = async () => {
    const validateCurrentPage = taskModalPageValidationRef.current;

    if (!validateCurrentPage) return true;

    return await validateCurrentPage();
  };

  const getChangedStepIds = (task: AutopilotTaskDto | undefined) => {
    const persistedTask = persistedTasksRef.current.find((candidate) => candidate.id === task?.id);

    return (
      task?.taskSteps
        ?.filter((step) => {
          const persistedStep = persistedTask?.taskSteps?.find(
            (candidate) => candidate.order === step.order
          );

          return (persistedStep?.isCompleted ?? false) !== (step.isCompleted ?? false);
        })
        .map((step) => step.order ?? 0) ?? []
    );
  };

  const persistTaskSteps = async (task: AutopilotTaskDto | undefined) => {
    const changedStepIds = getChangedStepIds(task);

    if (changedStepIds.length === 0 || !task?.id) return;

    persistingTaskRef.current = task;
    await toggleTaskSteps({
      taskId: task.id,
      stepIds: changedStepIds,
    });
    await invalidateTasks(campaignId, task.contractId);
  };

  const persistAllPages = async () => {
    const persistenceTasks = localTasks;

    await onSaveAllPages?.(persistenceTasks);

    for (const task of persistenceTasks) {
      await persistTaskSteps(task);
    }
  };

  const onPrevious = async () => {
    if (isPersistingCurrentPageRef.current) return;

    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const onNext = async () => {
    if (isPersistingCurrentPageRef.current) return;

    isPersistingCurrentPageRef.current = true;
    setIsPersistingCurrentPage(true);
    try {
      if (!(await isCurrentPageValid())) return;
      if (!(await canPersistCurrentPage())) return;

      setCurrentPage((previousPage) => {
        const nextPage = Math.min(previousPage + 1, pageTasks.length - 1);
        setHighestVisitedPage((visitedPage) => Math.max(visitedPage, nextPage));
        return nextPage;
      });
    } finally {
      isPersistingCurrentPageRef.current = false;
      setIsPersistingCurrentPage(false);
    }
  };

  const onSaveAndExit = async () => {
    if (isPersistingCurrentPageRef.current || completionReady) return;

    isPersistingCurrentPageRef.current = true;
    setIsPersistingCurrentPage(true);
    try {
      if (!(await canPersistCurrentPage())) return;

      await persistAllPages();
      onClose();
    } catch (error) {
      if (isWorkflowConcurrencyError(error)) return;

      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      });
    } finally {
      isPersistingCurrentPageRef.current = false;
      setIsPersistingCurrentPage(false);
    }
  };

  const onComplete = async () => {
    if (isPersistingCurrentPageRef.current) return;

    isPersistingCurrentPageRef.current = true;
    setIsPersistingCurrentPage(true);
    try {
      if (!(await isCurrentPageValid())) return;
      if (!(await canPersistCurrentPage())) return;
      if (!completionReady) return;

      setCompletionLoaderState(AgxAnimatedToggleLoaderState.Loading);
      await persistAllPages();
      setCompletionLoaderState(AgxAnimatedToggleLoaderState.Complete);
      onCompleteSuccess?.();
    } catch (error) {
      setCompletionLoaderState(undefined);

      if (isWorkflowConcurrencyError(error)) return;

      pushToast({
        type: 'error',
        message: 'Failed to save task changes',
      });
    } finally {
      isPersistingCurrentPageRef.current = false;
      setIsPersistingCurrentPage(false);
    }
  };

  const onCompletionLoaderComplete = () => {
    setCompletionLoaderState(undefined);
    onClose();
  };

  const shouldRenderPage = open && currentTask && (!isPending || isPersistingCurrentPage);
  const shouldRenderLoading = open && isPending && !isPersistingCurrentPage;
  const pageContent = shouldRenderPage ? (
    <TaskModalPage
      renderPage={renderPage}
      pageProps={{
        task: currentTask,
        tasks: localTasks,
        taskNumber: currentPage + 1,
        campaign,
        toggleTaskStep,
        toggleTaskStepForTask,
        registerBeforeTaskModalSave,
        registerTaskModalPageValidation,
      }}
    />
  ) : null;

  const loaderLabel =
    completionLoaderState === AgxAnimatedToggleLoaderState.Complete
      ? 'Complete'
      : completionLoaderState === AgxAnimatedToggleLoaderState.Loading
        ? 'Completing task...'
        : 'Saving progress...';
  const indicatorStates = completedPages.map<StepsIndicatorState>((isComplete, index) => {
    if (index > highestVisitedPage) return 'upcoming';
    if (!isComplete) return 'incomplete';
    if (index === currentPage) return 'current';
    return 'complete';
  });

  return (
    <AgxModal
      id={modalId}
      type="fullScreen"
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={onClose}
      isOpen={open}
      hideCloseButton={hideCloseButton}
      className={presentationClassName}
      secondaryTitle={
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <div className="taskModalHeader">
            <div className="taskModalHeader__title">
              <AgxTypography
                type="body_ui_strong"
                size="medium"
                id="titlemodal"
                color="var(--text-primary)"
              >
                {title}
              </AgxTypography>
              {titleTask?.dueUtc && (
                <TaskDueDateBadge
                  dueDate={titleTask.dueUtc}
                  isCompleted={titleTask.isCompleted ?? false}
                />
              )}
            </div>
            <div className="taskModalHeader__steps">
              <AgxStepsIndicator
                totalLevels={pageTasks.length}
                currentLevel={currentPage + 1}
                states={indicatorStates}
              />
            </div>
          </div>
        </AgxSpacer>
      }
      content={
        <>
          {(isPersistingCurrentPage || completionLoaderState) && (
            <AgxAnimatedToggleLoaderOverlay
              background="transparent"
              label={loaderLabel}
              onComplete={onCompletionLoaderComplete}
              state={completionLoaderState ?? AgxAnimatedToggleLoaderState.Loading}
              completionDurationMs={1000}
            />
          )}
          <AgxSpacer
            className="taskModalBody"
            direction="horizontal"
            padding={{
              top: 'md',
              bottom: 'md',
              left: 'xl',
              right: 'xl',
            }}
            fill
          >
            <div className="taskModalContentGrid">
              <div className="taskModalContentGrid__column taskModalContentGrid__column--files">
                <TasksFilesList files={documents} id={campaignId} />
              </div>
              <div className="taskModalContentGrid__column taskModalContentGrid__column--form">
                <AgxSpacer direction="vertical" fill>
                  {pageContent}
                  {shouldRenderLoading ? <AgxLoadingState /> : null}
                </AgxSpacer>
              </div>
            </div>
          </AgxSpacer>
        </>
      }
      actionButtons={
        <TaskModalActionButtons
          currentPage={currentPage}
          totalPages={pageTasks.length}
          onPrevious={onPrevious}
          onNext={onNext}
          onComplete={onComplete}
          onSaveAndExit={onSaveAndExit}
          completedPages={completedPages}
          completeDisabled={!completionReady}
          saveAndExitDisabled={saveAndExitDisabled || completionReady}
          isSaving={isPersistingCurrentPage}
          previousButtonText={previousButtonText}
        />
      }
    />
  );
};

export default TaskModalShell;
